import React, { useEffect } from 'react';
import { ButtonGroup, ButtonGroupTypes } from 'devextreme-react/button-group';
import { SingleMultipleOrNone } from 'devextreme/ui/button_group';
import { Validator, RequiredRule } from 'devextreme-react/validator';
import { ValidatedEvent } from 'devextreme/ui/validator';
import { useState } from 'react';
import { InfoIconButton } from '../InfoIconButton';
import '../../../scss/controls/_togglebutton.scss';

export enum ToggleButtonLabelSize {
    None,
    Small,
    SmallBold,
    Medium,
    Large,
}

/** Where the label sits relative to the buttons. */
export enum ToggleButtonLayoutStyle {
    /** Label above the buttons (default; the original layout). */
    Vertical = 'vertical',
    /** Label to the left of the buttons, in a fixed-width column (see labelWidth). */
    Horizontal = 'horizontal',
}

/** Vertical margin around the whole toggle (same values as the other controls' componentSpacing). */
export enum ToggleButtonComponentSpacing {
    None = 'noSpacing',
    SmallSpacing = 'smallSpacing',
    StandardSpacing = 'standardSpacing',
    LargeSpacing = 'largeSpacing',
}

export type ToggleButtonItem = {
    id: string;
    icon?: string;
    text: string;
    alignment?: string;
    hint?: string;
    disabled?: boolean;
};

export interface IToggleButtonOptions {
    id: string;
    items: ToggleButtonItem[];
    selectedItems?: string[];
    selectionMode: SingleMultipleOrNone | undefined;
    stylingMode: ButtonGroupTypes.ButtonStyle;
    onItemClick?(e: ButtonGroupTypes.ItemClickEvent): void;
    width?: string;
    height?: string;
    required?: boolean;
    requiredMessage?: string;
    validationGroup?: string;
    helperText?: string;
    label?: any;
    labelSize?: ToggleButtonLabelSize;
    showInfoIcon?: boolean;
    infoText?: React.ReactNode;
    /** Label above the buttons (Vertical, default) or beside them (Horizontal). In Horizontal, labelSize is ignored. */
    layoutStyle?: ToggleButtonLayoutStyle;
    /** Width of the label column when layoutStyle is Horizontal. Defaults to 120px. Ignored for Vertical. */
    labelWidth?: string;
    /** Vertical margin around the whole toggle. Omit for no margin (the original behaviour). */
    componentSpacing?: ToggleButtonComponentSpacing;
}

export const ToggleButton: React.FC<IToggleButtonOptions> = ({
    id,
    width = 'auto',
    height = 'auto',
    items,
    selectedItems,
    selectionMode,
    stylingMode,
    onItemClick,
    required = false,
    requiredMessage = 'This field is required.',
    helperText,
    label,
    labelSize = ToggleButtonLabelSize.None,
    showInfoIcon = false,
    infoText,
    layoutStyle = ToggleButtonLayoutStyle.Vertical,
    labelWidth,
    componentSpacing,
}) => {
    const [changeTriggered, set_changeTriggered] = useState(false);
    const [errorMessage, set_errorMessage] = useState('');
    const callbacks: any[] = [];

    const onValidated = (validatedInfo: ValidatedEvent) => {
        set_errorMessage(validatedInfo?.brokenRule?.message ?? '');
    };

    const adapterConfig = {
        getValue: () => {
            return selectedItems && selectedItems.length >= 1 && selectedItems[0] != '';
        },
        applyValidationResults: onValidated,
        validationRequestsCallbacks: callbacks,
    };

    useEffect(() => {
        if (changeTriggered) {
            if (callbacks && callbacks.length > 0) {
                callbacks.forEach((func) => {
                    func();
                });
            }
        }
    }, [selectedItems]);

    const handleItemClick = (e) => {
        if (onItemClick) {
            onItemClick(e);
        }
        set_changeTriggered(true);
    };

    const getLabelClassName = () => {
        switch (labelSize) {
            case ToggleButtonLabelSize.Small:
                return 'togglebutton-labelSmall';
            case ToggleButtonLabelSize.SmallBold:
                return 'togglebutton-labelSmallBold';
            case ToggleButtonLabelSize.Medium:
                return 'togglebutton-labelMedium';
            case ToggleButtonLabelSize.Large:
                return 'togglebutton-labelLarge';
            case ToggleButtonLabelSize.None:
                return;
            default:
                return;
        }
    };

    const getControlSpacing = () => {
        switch (labelSize) {
            case ToggleButtonLabelSize.Small:
            case ToggleButtonLabelSize.SmallBold:
                return '5px';
            case ToggleButtonLabelSize.Medium:
                return '10px';
            case ToggleButtonLabelSize.Large:
                return '20px';
            case ToggleButtonLabelSize.None:
                return;
            default:
                return;
        }
    };

    const renderButtonGroup = (marginTop: string | undefined) => (
        <div
            className={`togglebuttongroup-container ${errorMessage ? 'error' : ''}`}
            style={{ marginTop: marginTop }}
        >
            <ButtonGroup
                id={id}
                items={items}
                keyExpr='id'
                selectionMode={selectionMode}
                stylingMode={stylingMode}
                selectedItemKeys={selectedItems}
                onItemClick={handleItemClick}
                width={width}
                height={height}
                className={`togglebuttongroup-input ${errorMessage ? 'error' : ''}`}
            />
            <Validator adapter={adapterConfig}>{required && <RequiredRule message={requiredMessage} />}</Validator>
            {/* Show helper text only if there is no error */}
            {errorMessage ? (
                <div className='togglebuttongroup-error'>{errorMessage}</div>
            ) : (
                helperText && <div className='togglebuttongroup-helper'>{helperText}</div>
            )}
        </div>
    );

    // Opt-in: label in a fixed-width column to the left, buttons beside it. The label cell is always rendered so the
    // buttons stay in the second column (aligned with other horizontal fields) even when there is no label.
    if (layoutStyle === ToggleButtonLayoutStyle.Horizontal) {
        return (
            <div
                className={`togglebutton-horizontal${componentSpacing ? ` ${componentSpacing}` : ''}`}
                style={labelWidth ? ({ '--togglebutton-label-width': labelWidth } as React.CSSProperties) : undefined}
            >
                <div className={`togglebutton-horizontal-label${errorMessage ? ' error' : ''}`}>
                    {label}
                    {label && required ? ' *' : ''}
                    {label && showInfoIcon && <InfoIconButton modalText={infoText} />}
                </div>
                <div className='togglebutton-horizontal-control'>{renderButtonGroup(undefined)}</div>
            </div>
        );
    }

    // Vertical (default): exactly the original markup.
    const verticalContent = (
        <>
            {label && (
                <div className={getLabelClassName()}>
                    {label}
                    {required ? ' *' : ''}
                    {showInfoIcon && <InfoIconButton modalText={infoText} />}
                </div>
            )}
            {renderButtonGroup(getControlSpacing())}
        </>
    );

    // The vertical layout has no root element, so a wrapper is added only when spacing is asked for.
    return componentSpacing ? (
        <div className={`togglebutton-spacing ${componentSpacing}`}>{verticalContent}</div>
    ) : (
        verticalContent
    );
};
